import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../primitives';

/** `ENDED` once ElevenLabs reports the call over (its post-call webhook). */
export const AI_PHONE_CALL_STATUSES = ['IN_PROGRESS', 'ENDED'] as const;
export const zAiPhoneCallStatus = z.literal(AI_PHONE_CALL_STATUSES);
export type AiPhoneCallStatus = z.output<typeof zAiPhoneCallStatus>;

/**
 * One line of the caller's cart: what they chose, as a consumer app's cart item (`CartItemInput`),
 * plus the id the agent names it by. Names and prices are never stored: the server prices the cart
 * from the live menu every time it is read (`cartToOrder`).
 */
export const aiPhoneCartLineSchema = z.object({
  /** `"1"`, `"2"`, …: short, so the agent can say and repeat it. Never reused within a call. */
  lineId: z.string(),
  menuItemUuid: z.string(),
  modifierUuids: z.array(z.string()),
  qty: z.number(),
  notes: z.string().nullable(),
});
export type AiPhoneCartLine = z.output<typeof aiPhoneCartLineSchema>;

/**
 * One call to a venue's AI phone number, from the moment ElevenLabs connects it: the venue, the
 * caller, the cart and the order it placed. Created by the call-started webhook from the number
 * dialled, so the venue never comes from the model. Tools find it by ElevenLabs'
 * `conversationId`, which ElevenLabs fills into each tool call itself.
 *
 * The transcript and audio stay in ElevenLabs, looked up by `conversationId`. What happened on
 * the call goes to PostHog as `ai_phone_*` events with `order_uuid` set to `orderUuid`.
 */
export const aiPhoneCallSchema = z.object({
  /** ElevenLabs' `conversation_id`: unguessable, and only this call's agent knows it. */
  conversationId: z.string(),
  restaurantId: z.string(),
  /** The venue's number the caller dialled (`aiPhoneSettings.agentPhoneNumber`). */
  agentPhoneNumber: z.string(),
  /** The caller's number in E.164, or `null` when they withheld it. */
  callerPhoneNumber: z.string().nullable().default(null),
  cartLines: z.array(aiPhoneCartLineSchema).default([]),
  /** The next `lineId` to hand out. */
  nextLineId: z.number().default(1),
  /**
   * The cart total in cents the agent was last told, so the order is never placed at a total the
   * caller didn't hear (a price changed mid-call). `null` before the first cart change.
   */
  lastQuotedTotal: z.number().nullable().default(null),
  /** The order's uuid, set when the call starts: one order per call, so it's the idempotency key. */
  orderUuid: z.string(),
  /** The order the call placed, or `null` until it places one. */
  orderId: z.string().nullable().default(null),
  status: zAiPhoneCallStatus.default('IN_PROGRESS'),
  /** ElevenLabs' summary of the call, from its post-call webhook. */
  summary: z.string().nullable().default(null),
  /**
   * How well the call went for the caller, 0 to 100: ElevenLabs' post-call analysis scores it
   * (`success_score`, set by `aiPhoneAgent/deployToElevenLabs.ts`). `null` until the call ends, or
   * when ElevenLabs couldn't score it.
   */
  successScore: z.number().nullable().default(null),
  /**
   * ElevenLabs' read of the caller's mood over the call: `positive`, `neutral` or `negative`.
   * `null` until the call ends, or when the caller said nothing.
   */
  sentiment: z.string().nullable().default(null),
  startedAt: z.number(),
  endedAt: z.number().nullable().default(null),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type AiPhoneCall = z.output<typeof aiPhoneCallSchema>;

/** Strict schema */
export const aiPhoneCallStrictSchema = aiPhoneCallSchema;

/** Convex schema */
export const aiPhoneCallCvxSchema = aiPhoneCallSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type AiPhoneCallCvx = z.output<typeof aiPhoneCallCvxSchema>;
assertTypes<NoNewFields<AiPhoneCall, AiPhoneCallCvx>>();
